import React, { useState, useEffect } from "react";
import {
  Search,
  Plus,
  Trash2,
  Share,
  Files,
  Upload,
  RotateCw,
  Eye,
  FileUp,
  Filter,
  X,
  Star,
  Edit,
  Building2,
} from "lucide-react";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { toast } from "sonner";
import { useUser } from "../../../../context/UserContext";
import { useCreatePermissions } from "../../../../hooks/useCreatePermissions";
import {
  listKnowledgeBases,
  listKnowledgeBasesPaginated,
  deleteKnowledgeBase,
} from "../../../../service/apiService";
import MobileBBView from "./MobileBBView";
import LockButton from "../../../Lock/LockButton";
import LockBadge from "../../../Lock/LockBadge";

// Define BrainBank type
interface BrainBank {
  id: string;
  knowledgebase_name: string;
  index_name: string;
  description: string;
  department?: string;
  file_ids: string[];
  last_sync: string;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
  summary: string | null;
  // Admin lock. Only ever set on rows the viewer locked themselves -- the API
  // hides everyone else's locked BrainBanks from this list entirely.
  locked_by?: string | null;
  locked_at?: string | null;
  files?: Array<{
    id: string;
    file_name: string;
    status: string;
    created_by: string;
    created_at: string;
  }>;
}

type ViewType = 'my_knowledge_base' | 'assigned_knowledge_base' | 'all';

interface BrainBankListViewProps {
  onCreateClick: () => void;
  onEditClick: (brainBank: BrainBank) => void;
  onViewFilesClick: (brainBank: BrainBank) => void;
  onUploadClick: (brainBank: BrainBank) => void;
  refreshKey: number;
}

const BrainBankListView: React.FC<BrainBankListViewProps> = ({
  onCreateClick,
  onEditClick,
  onViewFilesClick,
  onUploadClick,
  refreshKey
}) => {

  const { userId, userDetails } = useUser();
  const isAdmin = userDetails?.role !== 'user';
  const { canCreateBrainBank } = useCreatePermissions();
  const [searchQuery, setSearchQuery] = useState("");
  const [brainBanks, setBrainBanks] = useState<BrainBank[]>([]);
  const [loading, setLoading] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [hoveredIcon, setHoveredIcon] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [isMobileView, setIsMobileView] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [isFilterLoading, setIsFilterLoading] = useState(false);
  const [currentFilter, setCurrentFilter] = useState<ViewType>(
  isAdmin ? 'all' : 'my_knowledge_base'
);
const [tempFilter, setTempFilter] = useState<ViewType>(
  isAdmin ? 'all' : 'my_knowledge_base'
);
  
  // Detect mobile view
  useEffect(() => {
    const checkMobileView = () => {
      setIsMobileView(window.innerWidth < 768);
    };
    
    checkMobileView();
    window.addEventListener('resize', checkMobileView);
    
    return () => {
      window.removeEventListener('resize', checkMobileView);
    };
  }, []);

  // Load BrainBanks
  useEffect(() => {
    if (userId) {
      fetchBrainBanks();
    }
  }, [refreshKey, currentFilter, isAdmin, userId]);
  
  const fetchBrainBanks = async () => {
    setLoading(true);
    try {
      if (isAdmin && currentFilter === 'all') {
        // Admin sees all BrainBanks
        const result = await listKnowledgeBases();
        setBrainBanks(Array.isArray(result) ? result : []);
      } else {
        // Regular users see filtered BrainBanks
        const result = await listKnowledgeBasesPaginated(1, 100, currentFilter as 'my_knowledge_base' | 'assigned_knowledge_base');
        setBrainBanks(result?.knowledge_bases || []);
      }
    } catch (error) {
      console.error("Error fetching BrainBanks:", error);
      toast.error("Failed to load BrainBanks");
      setBrainBanks([]);
    } finally {
      setLoading(false);
      setIsFilterLoading(false);
    }
  };
  
  // Filtered BrainBanks based on search
  const filteredBrainBanks = React.useMemo(() => {
    if (!searchQuery.trim()) return brainBanks;
    
    const query = searchQuery.toLowerCase();
    return brainBanks.filter((bb: BrainBank) => 
      bb.knowledgebase_name.toLowerCase().includes(query) || 
      bb.description?.toLowerCase().includes(query)
    );
  }, [brainBanks, searchQuery]);

  // Handle filter modal open
  const handleFilterClick = () => {
    setTempFilter(currentFilter);
    setShowFilterModal(true);
  };

  // Handle filter apply
  const handleApplyFilter = async () => {
    if (tempFilter !== currentFilter) {
      setIsFilterLoading(true);
      setShowFilterModal(false);
      
      try {
        setCurrentFilter(tempFilter);
        setSearchQuery(""); 
      } catch (error) {
        console.error("Error applying filter:", error);
        toast.error("Failed to apply filter");
      }
    } else {
      setShowFilterModal(false);
    }
  };

  // Also update the handleClearFilters function:
const handleClearFilters = async () => {
  const defaultFilter: ViewType = isAdmin ? "all" : "my_knowledge_base";
  if (currentFilter !== defaultFilter) {
    setIsFilterLoading(true);
    setShowFilterModal(false);
    
    try {
      setTempFilter(defaultFilter);
      setCurrentFilter(defaultFilter);
      setSearchQuery("");
    } catch (error) {
      console.error("Error clearing filters:", error);
      toast.error("Failed to clear filters");
    }
  } else {
    setTempFilter(defaultFilter);
    setShowFilterModal(false);
  }
};
  // Get filter display text
  const getFilterDisplayText = () => {
    switch (currentFilter) {
      case 'my_knowledge_base':
        return 'My BrainBanks';
      case 'assigned_knowledge_base':
        return 'Assigned BrainBanks';
      case 'all':
        return 'All BrainBanks';
      default:
        return 'All BrainBanks';
    }
  };
  
  // Handle tooltip display
  const handleIconMouseEnter = (e: React.MouseEvent, iconType: string) => {
    if (!confirmDeleteId) {
      setHoveredIcon(iconType);
      updateTooltipPosition(e);
    }
  };
  
  const handleIconMouseLeave = () => {
    setHoveredIcon(null);
  };
  
  const handleIconMouseMove = (e: React.MouseEvent) => {
    if (hoveredIcon && !confirmDeleteId) {
      updateTooltipPosition(e);
    }
  };
  
  const updateTooltipPosition = (e: React.MouseEvent) => {
    setTooltipPosition({
      top: e.clientY + 10,
      left: e.clientX + 10
    });
  };
  
  const handleDeleteClick = (brainBank: BrainBank, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setHoveredIcon(null);
    if (brainBank.id) {
      setConfirmDeleteId(brainBank.id);
    } else {
      toast.error("Cannot delete: Missing BrainBank ID");
    }
  };
  
  const handleDelete = async (brainBankId: string) => {
    if (!brainBankId) {
      toast.error("Error: No BrainBank selected for deletion");
      return;
    }
    
    setIsDeleting(true);
    try {
      await deleteKnowledgeBase(brainBankId);
      setConfirmDeleteId(null);
      toast.success("BrainBank deleted successfully");
      fetchBrainBanks(); 
    } catch (err: any) {
      console.error("Error deleting BrainBank:", err);
      if (err?.response?.data?.detail) {
        toast.error(err.response.data.detail);
      } else if (err?.message) {
        toast.error(err.message);
      } else {
        toast.error("Failed to delete BrainBank");
      }
    } finally {
      setIsDeleting(false);
    }
  };
  
  const getBrainBankType = (brainBank: BrainBank, currentUserId: string) => {
    const isOwner = brainBank.created_by === currentUserId;
    
    if (isOwner) {
      return "My BrainBank";
    } else {
      return "Assigned BrainBank";
    }
  };

  // Render mobile view if screen size is small
  if (isMobileView) {
    return (
      <MobileBBView
        onCreateClick={onCreateClick}
        onEditClick={onEditClick}
        onViewFilesClick={onViewFilesClick}
        onUploadClick={onUploadClick}
        refreshKey={refreshKey}
      />
    );
  }
  
  const columnCount = isAdmin ? 5 : 6;
  const columnWidth = `${100 / columnCount}%`;
  
  const tableStyles = {
    tableLayout: 'fixed' as const,
    width: '100%'
  };
  
  const cellStyles = {
    width: columnWidth
  };

  return (
    <>
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {/* Header with search and create button */}
        <div className="p-4 flex items-center justify-between border-b">
          <div className="flex items-center space-x-4 flex-1">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-2 bg-white text-gray-900 border border-gray-200 rounded-md text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                placeholder="Search BrainBanks"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            
            {/* Filter Dropdown for Regular Users */}
            {!isAdmin && (
              <button
                onClick={handleFilterClick}
                disabled={isFilterLoading}
                className={`flex items-center justify-between px-3 py-2 text-sm border border-gray-200 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-violet-500 min-w-[140px] ${
                  isFilterLoading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isFilterLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-violet-600 mr-2"></div>
                    <span>Loading...</span>
                  </div>
                ) : (
                  <>
                    <span>{getFilterDisplayText()}</span>
                    <Filter size={14} className="ml-2 text-gray-400" />
                  </>
                )}
              </button>
            )}
          </div>

          {canCreateBrainBank && (
            <button
              onClick={onCreateClick}
              className="bg-violet-600 hover:bg-violet-700 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create BrainBank
            </button>
          )}
        </div>
        
        {/* BrainBank Table with equal column widths */}
        <div className="w-full">
          <table className="min-w-full divide-y divide-gray-200" style={tableStyles}>
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={cellStyles}>
                  Name
                </th>
              <th scope="col" className="pl-11 pr-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={cellStyles}>
                Team
               </th>
               <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={cellStyles}>
               Department
               </th>
                {!isAdmin && (
                  <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={cellStyles}>
                    Type
                  </th>
                )}
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={cellStyles}>
                  Summary
                </th>
                <th scope="col" className="pl-14 pr-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={cellStyles}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading || isFilterLoading ? (
                <tr>
                  <td colSpan={columnCount} className="px-3 py-4 text-center text-sm text-gray-500">
                    <div className="flex items-center justify-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-violet-600"></div>
                      <span>{isFilterLoading ? "Applying filter..." : "Loading BrainBanks..."}</span>
                    </div>
                  </td>
                </tr>
              ) : filteredBrainBanks.length === 0 ? (
                <tr>
                  <td colSpan={columnCount} className="px-3 py-4 text-center text-sm text-gray-500">
                    {searchQuery 
                      ? "No BrainBanks match your search" 
                      : "No BrainBanks found"}
                  </td>
                </tr>
              ) : (
                filteredBrainBanks.map((brainBank: BrainBank) => (
                  <tr 
                    key={brainBank.id} 
                    className="hover:bg-gray-50"
                  >
                    <td className="px-3 py-4" style={cellStyles}>
  <div className="flex items-center gap-2 min-w-0">
    <span className="text-sm font-medium text-gray-900 truncate">
      {brainBank.knowledgebase_name}
    </span>
    {brainBank.locked_by && (
      <LockBadge
        className="flex-shrink-0"
        label={
          brainBank.locked_at
            ? `Locked on ${new Date(brainBank.locked_at).toLocaleDateString()}`
            : "Locked"
        }
      />
    )}
  </div>
</td>
<td className="pl-11 pr-3 py-4" style={cellStyles}>
  <div className="text-sm text-gray-500">
    Private
  </div>
</td>
<td className="px-3 py-4" style={cellStyles}>
  <div className="flex items-center">
    {brainBank.department ? (
      <>
        <Building2 className="text-violet-600 mr-2 flex-shrink-0" size={16} />
        <span className="text-sm text-gray-900 truncate">{brainBank.department}</span>
      </>
    ) : (
      <span className="text-sm text-gray-500 italic">No Department</span>
    )}
  </div>
</td>
{!isAdmin && (
  <td className="px-3 py-4" style={cellStyles}>
    <div className="text-sm text-gray-800">
      {getBrainBankType(brainBank, userId)}
    </div>
  </td>
)}
                    <td className="px-3 py-4" style={cellStyles}>
                      <div className="text-sm text-gray-900 line-clamp-2">
                        {brainBank.description || "No description"}
                      </div>
                    </td>
                    <td className="pl-11 pr-25" style={cellStyles}>
                      {confirmDeleteId === brainBank.id ? (
                        <div className="flex flex-col space-y-1 min-w-0">
                          <div className="text-xs text-gray-700 whitespace-normal">
                           You will lose all data and files.
                          </div>
                          <div className="flex space-x-1 flex-shrink-0">
                            <button
                              onClick={() => handleDelete(brainBank.id || "")}
                              className="rounded-md bg-red-600 px-2 py-1 text-xs font-medium text-white hover:bg-red-700 focus:outline-none"
                              disabled={isDeleting}
                              aria-label="Confirm Delete"
                            >
                              {isDeleting ? "..." : "Yes"}
                            </button>
                            {!isDeleting && (
                              <button
                                onClick={() => setConfirmDeleteId(null)}
                                className="rounded-md bg-gray-200 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-300 focus:outline-none"
                                aria-label="Cancel Delete"
                              >
                                No
                              </button>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-1">
                          <button 
                          onClick={() => onViewFilesClick(brainBank)}
                          className="p-1 text-blue-600 hover:text-blue-800 rounded-full focus:outline-none"
                          aria-label="View Files"
                          onMouseEnter={(e) => handleIconMouseEnter(e, "view")}
                          onMouseLeave={handleIconMouseLeave}
                          onMouseMove={handleIconMouseMove}
                         >
                       <Eye className="h-4 w-4" />
                     </button>
  
                    {/* Edit + Delete -- hidden when admin turned off BrainBank creation */}
                    {canCreateBrainBank && (
                    <>
                    <button
                     onClick={() => onEditClick(brainBank)}
                     className="p-1 text-violet-600 hover:text-violet-800 rounded-full focus:outline-none"
                     aria-label="Edit BrainBank"
                     onMouseEnter={(e) => handleIconMouseEnter(e, "edit")}
                     onMouseLeave={handleIconMouseLeave}
                     onMouseMove={handleIconMouseMove}
                     >
                   <Edit className="h-4 w-4" />
                  </button>
  
                   <button 
                    onClick={(e) => handleDeleteClick(brainBank, e)}
                    className="p-1 text-red-600 hover:text-red-800 rounded-full focus:outline-none"
                    aria-label="Delete BrainBank"
                    onMouseEnter={(e) => handleIconMouseEnter(e, "delete")}
                    onMouseLeave={handleIconMouseLeave}
                    onMouseMove={handleIconMouseMove}
                  >
                {isDeleting && brainBank.id === confirmDeleteId ? (
                 <RotateCw className="animate-spin h-4 w-4" />
                ) : (
                    <Trash2 className="h-4 w-4" />
                   )}
               </button>
                    </>
                    )}

                   <LockButton
                     resourceType="brainbank"
                     resourceId={brainBank.id}
                     resource={brainBank}
                     resourceName={brainBank.knowledgebase_name}
                     ownerId={brainBank.created_by}
                     onChange={fetchBrainBanks}
                   />
                </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tooltip - only show when not in delete confirmation mode */}
      {hoveredIcon && !confirmDeleteId && (
        <div 
          className="fixed bg-white border border-gray-200 px-2 py-1 text-xs shadow-md z-50"
          style={{ 
            top: tooltipPosition.top + 'px', 
            left: tooltipPosition.left + 'px',
            fontSize: '12px',
            borderRadius: '2px'
          }}
        >
          {hoveredIcon === "view" && "View Files"}
          {hoveredIcon === "edit" && "Edit BrainBank"}
          {hoveredIcon === "delete" && "Delete"}
        </div>
      )}

      {/* Filter Modal - Only for non-admin users */}
      {!isAdmin && (
        <Transition appear show={showFilterModal} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={() => setShowFilterModal(false)}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black bg-opacity-25" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4 text-center">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                    <div className="flex justify-between items-center mb-4">
                      <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                        Filter BrainBanks
                      </Dialog.Title>
                      <button
                        onClick={() => setShowFilterModal(false)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <X size={20} />
                      </button>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <h4 className="flex items-center text-sm font-medium text-gray-700 mb-3">
                          <Star size={16} className="mr-2 text-violet-500" />
                          Filters
                        </h4>
                        
<div className="space-y-2">
  {/* Only show 'All BrainBanks' option for admins */}
  {isAdmin && (
    <label className="flex items-center">
      <input
        type="radio"
        name="filter"
        value="all"
        checked={tempFilter === 'all'}
        onChange={(e) => setTempFilter(e.target.value as ViewType)}
        className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300"
      />
      <span className="ml-2 text-sm text-gray-700">All BrainBanks</span>
    </label>
  )}
  
  <label className="flex items-center">
    <input
      type="radio"
      name="filter"
      value="my_knowledge_base"
      checked={tempFilter === 'my_knowledge_base'}
      onChange={(e) => setTempFilter(e.target.value as ViewType)}
      className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300"
    />
    <span className="ml-2 text-sm text-gray-700">My BrainBanks</span>
  </label>
  
  <label className="flex items-center">
    <input
      type="radio"
      name="filter"
      value="assigned_knowledge_base"
      checked={tempFilter === 'assigned_knowledge_base'}
      onChange={(e) => setTempFilter(e.target.value as ViewType)}
      className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300"
    />
    <span className="ml-2 text-sm text-gray-700">Assigned BrainBanks</span>
  </label>
</div>
                      </div>
                    </div>

                    <div className="mt-6 flex justify-between">
                      <button
                        type="button"
                        className="inline-flex justify-center rounded-md border border-transparent bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2"
                        onClick={handleClearFilters}
                      >
                        Clear all filters
                      </button>
                      <button
                        type="button"
                        className="inline-flex justify-center rounded-md border border-transparent bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
                        onClick={handleApplyFilter}
                      >
                        Done
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      )}
    </>
  );
};

export default BrainBankListView;